import { api } from "./api";

/**
 * The open-data licence: what the register's files are published under, and
 * whether anybody here has accepted it.
 *
 * The terms are FACTS and come from the server (URLs, a licence name, an issue
 * version). The prose around them lives in the message catalogues, because it
 * exists in two languages and the importer that reads the same catalogue has no
 * i18n at all.
 */
export type OpenDataSource = "rik" | "emta" | "mtr" | "rhr";

/** Why the gate holds. Mirrors the server's `LicenceBlock`. */
export type LicenceBlock = "missing" | "stale" | "other_terms";

export interface OpenDataTermsView {
  id: string;
  version: string;
  termsUrl: string;
  downloadUrl: string;
  licenceName: string;
  licenceUrl: string;
  attributionRequired: boolean;
  shareAlike: boolean;
}

export interface LicenceAcceptanceView {
  termsId: string;
  version: string;
  termsUrl: string;
  licenceName: string;
  acceptedAt: string;
  acceptedBy: { id: string | null; name: string; email: string } | null;
}

export interface LicenceSourceView {
  source: OpenDataSource;
  host: string;
  infoUrl: string;
  /** Null when nobody has established what this publisher licenses under. */
  terms: OpenDataTermsView | null;
  accepted: LicenceAcceptanceView | null;
  /** Whether this source's downloads run. */
  ok: boolean;
  block: LicenceBlock | null;
}

export const getOpenDataLicence = (): Promise<{ sources: LicenceSourceView[] }> =>
  api.get("/admin/crm/ariregister/licence");

/**
 * Accept, echoing back the exact issue that was on screen.
 *
 * The server refuses a mismatch rather than accepting whatever is current.
 * Agreeing to terms you were never shown is not agreement, and the way that
 * happens is a tab left open across a deploy that changed them.
 */
export const acceptOpenDataLicence = (body: {
  source: OpenDataSource;
  termsId: string;
  version: string;
}): Promise<{ ok: boolean }> => api.post("/admin/crm/ariregister/licence/accept", body);

export const withdrawOpenDataLicence = (source: OpenDataSource): Promise<{ ok: boolean }> =>
  api.post("/admin/crm/ariregister/licence/withdraw", { source });
